import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { Radar, Doughnut } from 'react-chartjs-2';
import { DimensionDetails } from './DimensionDetails';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

const dimensionsData = [
  {
    name: 'Communication',
    summary: 'Your communication is clear and effective, but evaluators noted occasional assertiveness that may come across as pushy. Refining your tone and delivery could enhance message receptiveness.',
    selfScore: 3.5,
    evaluatorScore: 2.0,
    feedback: [
      'Your clarity in presenting ideas is highly appreciated.',
      'Evaluators suggest toning down assertiveness for smoother interactions.',
      'A focus on active listening can further improve outcomes.'
    ],
    specificExamples: "Evaluators noted that your communication often appears clear and goal-oriented. However, a few instances highlighted a perceived lack of openness to feedback, which could hinder collaboration.",
    actionableRecommendations: [
      "Work on creating two-way communication channels by actively asking for input and feedback during discussions.",
      "Engage in active listening exercises to balance assertiveness with inclusivity.",
      "Consider communication coaching sessions to refine tone and messaging, ensuring clarity without being perceived as overly assertive."
    ],
    tag: 'Blind Spot'
  },
  {
    name: 'Collaboration',
    summary: 'You are an excellent team player who fosters collaboration and harmony. Peers value your approachability and ability to adapt to group dynamics.',
    selfScore: 4.5,
    evaluatorScore: 4.6,
    feedback: [
      'Peers feel supported by your inclusive approach.',
      'Your adaptability in group tasks is highly valued.',
      'Continue to foster open communication within teams for maximum impact.'
    ],
    specificExamples: "Your ability to foster collaboration is highly appreciated, as seen in a recent cross-departmental project where you encouraged all team members to contribute ideas. Peers highlighted your approachability and adaptability when resolving inter-team challenges.",
    actionableRecommendations: [
      "Continue building strong relationships by regularly seeking input from quieter team members to ensure inclusivity.",
      "Take the lead in organizing team-building activities to further enhance rapport among colleagues.",
      "Leverage collaboration tools to streamline communication and task management for group projects."
    ],
    tag: 'Key Strength'
  },
  {
    name: 'Initiative',
    summary: 'Your proactive nature and willingness to take charge are seen as strengths. While evaluators align with your view, focusing on balancing ambition with team dynamics can be beneficial.',
    selfScore: 3.0,
    evaluatorScore: 4.0,
    feedback: [
      'Your initiative drives projects forward efficiently.',
      'Some evaluators suggest ensuring alignment with team priorities.',
      'Leverage your initiative to inspire colleagues further.'
    ],
    specificExamples: "Your proactive approach was evident during a recent project where you stepped up to manage a potential risk. However, evaluators suggest balancing proactivity with seeking input to avoid overwhelming team dynamics.",
    actionableRecommendations: [
      "Set clear boundaries for your initiatives to ensure alignment with team objectives and priorities.",
      "Take a coaching or mentoring role to inspire colleagues and promote shared ownership of proactive decisions.",
      "Reflect on past actions to assess where collaboration could have been further integrated into your initiatives."
    ],
    tag: 'Hidden Strength'
  },
  {
    name: 'Problem-Solving',
    summary: 'Problem-solving is one of your standout strengths, with high scores across both self-assessment and evaluators. Your analytical approach and decisiveness are admired.',
    selfScore: 4.6,
    evaluatorScore: 4.5,
    feedback: [
      'Your ability to analyze and resolve complex issues is highly respected.',
      'Peers appreciate your decisive action during challenges.',
      'Enhancing collaboration during problem-solving can create broader solutions.'
    ],
    specificExamples: "Evaluators commended your analytical skills, especially during high-stakes situations like resolving technical bottlenecks in client presentations. However, there's an opportunity to further involve your team in brainstorming solutions for more comprehensive outcomes.",
    actionableRecommendations: [
      "Facilitate problem-solving workshops to encourage collective team input and share your decision-making process.",
      "Document successful resolutions and share lessons learned as best practices for future challenges.",
      "Attend advanced problem-solving or strategic thinking courses to further refine your approach."
    ]
  },
  {
    name: 'Leadership',
    summary: 'Leadership development remains an area for growth. Evaluators note a need to inspire and motivate team members, aligning with your own recognition of this opportunity.',
    selfScore: 3.0,
    evaluatorScore: 1.8,
    feedback: [
      'Peers feel your leadership can be more empowering.',
      'Focus on fostering a motivational environment for team members.',
      'Develop strategies to align team goals with broader organizational objectives.'
    ],
    specificExamples: "Leadership emerged as an area for improvement, with evaluators noting that more motivational engagement with your team is needed. For example, team members mentioned a lack of emotional connection when goals are communicated.",
    actionableRecommendations: [
      "Focus on motivational techniques, such as public recognition of team achievements and creating a shared vision for projects.",
      "Participate in leadership development programs to enhance skills in inspiring and empowering your team.",
      "Build stronger relationships with team members through one-on-one meetings to understand their unique goals and challenges."
    ]
  },
  {
    name: 'Performance',
    summary: 'Consistently high performance defines your professional contributions. Both you and evaluators agree on your ability to deliver high-quality results under pressure.',
    selfScore: 4.7,
    evaluatorScore: 4.8,
    feedback: [
      'Your consistent delivery of results is highly valued by peers.',
      'Maintain focus on performance quality while ensuring team alignment.',
      'Consider sharing best practices to uplift overall team performance.'
    ],
    specificExamples: "Both evaluators and self-assessment align in recognizing your consistent delivery of high-quality work, even under pressure. However, there were mentions of a tendency to focus excessively on perfection, potentially slowing task delegation.",
    actionableRecommendations: [
      "Develop time-management strategies to ensure timely task completion while maintaining quality.",
      "Delegate more effectively by creating clear guidelines or SOPs for repetitive tasks.",
      "Engage in performance optimization workshops to learn how to balance perfectionism with productivity."
    ]
  }
];

export function ResultsOverview() {
  const [showMore, setShowMore] = useState(false);
  const [showSkillsMore, setShowSkillsMore] = useState(false);
  const { currentUser } = useAuth();
  const [radarData, setRadarData] = useState<any>(null);
  const [skillsData, setSkillsData] = useState<any>(null);
  const [softHardSkillsData, setSoftHardSkillsData] = useState<any>(null);
  
  useEffect(() => {
    async function loadReportData() {
      if (!currentUser) return;
      
      try {
        const reportRef = doc(db, collections.reports, currentUser.uid);
        const reportDoc = await getDoc(reportRef);
        
        if (reportDoc.exists()) {
          const data = reportDoc.data();
          if (data.results_overview_radar_chart_scores) {
            setRadarData(JSON.parse(data.results_overview_radar_chart_scores));
          }
          if (data.results_overview_skills_detail) {
            setSkillsData(JSON.parse(data.results_overview_skills_detail));
          }
          if (data.results_overview_soft_hard_skills) {
            setSoftHardSkillsData(JSON.parse(data.results_overview_soft_hard_skills));
          }
        }
      } catch (error) {
        console.error('Error loading report data:', error);
      }
    }
    
    loadReportData();
  }, [currentUser]);

  const chartData = useMemo(() => {
    if (!radarData?.radarChart) return null;
    
    const dimensions = Object.keys(radarData.radarChart);
    const userScores = dimensions.map(d => radarData.radarChart[d].user);
    const evaluatorScores = dimensions.map(d => radarData.radarChart[d].evaluators);
    
    return {
      labels: dimensions,
      datasets: [
        {
          label: 'Self Assessment',
          data: userScores,
          backgroundColor: 'rgba(238, 153, 234, 0.2)',
          borderColor: 'rgb(238, 153, 234)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(238, 153, 234)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(238, 153, 234)',
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Evaluator Average',
          data: evaluatorScores,
          backgroundColor: 'rgba(88, 211, 226, 0.2)',
          borderColor: 'rgb(88, 211, 226)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(88, 211, 226)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(88, 211, 226)',
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };
  }, [radarData]);

  const data = {
    labels: ['Communication', 'Collaboration', 'Initiative', 'Problem-Solving', 'Leadership', 'Performance'],
    datasets: [
      {
        label: 'Self Assessment',
        data: [3.5, 4.5, 3.0, 4.6, 3.0, 4.7],
        backgroundColor: 'rgba(238, 153, 234, 0.2)',
        borderColor: 'rgb(238, 153, 234)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(238, 153, 234)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(238, 153, 234)',
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Evaluator Average',
        data: [2.0, 4.6, 4.0, 4.5, 1.8, 4.8],
        backgroundColor: 'rgba(88, 211, 226, 0.2)',
        borderColor: 'rgb(88, 211, 226)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(88, 211, 226)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(88, 211, 226)',
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: {
          stepSize: 1,
          backdropColor: 'transparent'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#000000',
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: (chart: any) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset: any, i: number) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              strokeStyle: dataset.borderColor,
              lineWidth: 0,
              hidden: !chart.isDatasetVisible(i),
              index: i,
              color: '#000000'
            }));
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${context.raw.toFixed(1)}`;
          }
        }
      }
    }
  };

  const softSkillsData = {
    labels: ['Score', 'Remaining'],
    datasets: [
      {
        label: 'Self Assessment',
        data: [3.5, 1.5],
        backgroundColor: [
          'rgba(238, 153, 234, 0.8)',
          'rgba(238, 153, 234, 0.1)'
        ],
        borderColor: [
          'rgb(238, 153, 234)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      },
      {
        label: 'Evaluators Average',
        data: [2.8, 2.2],
        backgroundColor: [
          'rgba(88, 211, 226, 0.8)',
          'rgba(88, 211, 226, 0.1)'
        ],
        borderColor: [
          'rgb(88, 211, 226)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      }
    ]
  };

  const hardSkillsData = {
    labels: ['Score', 'Remaining'],
    datasets: [
      {
        label: 'Self Assessment',
        data: [4.0, 1.0],
        backgroundColor: [
          'rgba(238, 153, 234, 0.8)',
          'rgba(238, 153, 234, 0.1)'
        ],
        borderColor: [
          'rgb(238, 153, 234)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      },
      {
        label: 'Evaluators Average',
        data: [4.2, 0.8],
        backgroundColor: [
          'rgba(88, 211, 226, 0.8)',
          'rgba(88, 211, 226, 0.1)'
        ],
        borderColor: [
          'rgb(88, 211, 226)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      }
    ]
  };

  const donutOptions = {
    responsive: true,
    cutout: '60%',
    layout: {
      padding: {
        top: 20,
        bottom: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          generateLabels: (chart: any) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset: any, i: number) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor[0],
              strokeStyle: dataset.borderColor[0],
              lineWidth: 0,
              hidden: !chart.isDatasetVisible(i),
              index: i
            }));
          }
        }
      },
      tooltip: {
        position: 'nearest',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        z: 9999,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            if (context.dataIndex === 1) return null;
            
            const score = context.raw.toFixed(1);
            const percentage = ((context.raw / 5) * 100).toFixed(0);
            const label = context.dataset.label;
            
            return [
              `Score: ${score} / 5`,
              `Percentage: ${percentage}%`,
              label === 'Self Assessment' 
                ? 'Your self-evaluated score'
                : 'Average score from evaluators'
            ];
          },
          afterLabel: function(context: any) {
            if (context.dataIndex === 1) return null;
            
            if (context.dataset.label === 'Evaluators Average') {
              const selfScore = context.chart.data.datasets[0].data[0];
              const evalScore = context.raw;
              const diff = (evalScore - selfScore).toFixed(1);
              const diffText = diff > 0 
                ? `${diff} points higher than your assessment`
                : diff < 0
                ? `${Math.abs(Number(diff))} points lower than your assessment`
                : 'Matches your self-assessment';
              return [`Comparison: ${diffText}`];
            }
            return null;
          }
        }
      }
    }
  };

  return (
    <div className="border-b border-gray-200">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Results Overview</h2>
        </div>

        <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
          <p className="text-gray-600 leading-relaxed mb-4">
            This overview provides a comprehensive comparison between your self-assessment and the aggregated feedback from your evaluators across key dimensions.
          </p>
          
          <motion.div
            initial={false}
            animate={{ height: showMore ? 'auto' : 0, opacity: showMore ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
              <p>The radar chart visualizes scores across six key dimensions:</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Communication: How effectively you convey ideas and engage with others</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Collaboration: Your ability to work with and support team members</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Initiative: How proactive you are in taking on responsibilities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Problem-Solving: Your approach to challenges and finding solutions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Leadership: Your capacity to guide and inspire others</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Performance: Your overall effectiveness and delivery of results</span>
                </li>
              </ul>
            </div>
          </motion.div>
          
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
          >
            <span>{showMore ? 'Read less' : 'Read more'}</span>
            <motion.svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ rotate: showMore ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </button>
        </div>

        <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
          <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
            <div className="h-[500px] w-full max-w-3xl mx-auto">
              {chartData && <Radar data={chartData} options={options} />}
            </div>
          </div>
        </div>
      </div>
      
      <DimensionDetails dimensions={skillsData?.dimensions} />

      <div className="px-6 py-8 border-t border-gray-200">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Hard Skills vs. Soft Skills</h2>
        </div>
        
        <div className="mb-8 bg-[#f9fafb] rounded-xl p-6">
          <p className="text-gray-600 leading-relaxed mb-4">
            Compare your technical expertise with interpersonal abilities to identify areas for balanced professional development.
          </p>
          
          <motion.div
            initial={false}
            animate={{ height: showSkillsMore ? 'auto' : 0, opacity: showSkillsMore ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 text-gray-600 leading-relaxed mb-4">
              <p>This analysis helps you understand:</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>How your technical and interpersonal skills compare</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Where to focus your development efforts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>How to achieve a balanced skill set for career advancement</span>
                </li>
              </ul>
            </div>
          </motion.div>
          
          <button
            onClick={() => setShowSkillsMore(!showSkillsMore)}
            className="text-primary hover:text-primary-hover font-medium flex items-center space-x-1"
          >
            <span>{showSkillsMore ? 'Read less' : 'Read more'}</span>
            <motion.svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ rotate: showSkillsMore ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </button>
        </div>

        {!softHardSkillsData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 rounded-xl p-8 text-center"
          >
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Skills Analysis Not Available
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto">
              The skills analysis will be available once all evaluations are completed and the report is generated.
            </p>
          </motion.div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
              <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                <div className="prose max-w-none text-gray-600 mb-8">
                  <p>{softHardSkillsData.introText}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Soft Skills Assessment</h3>
                <div className="h-[300px] relative">
                  <div className="absolute inset-0">
                    <Doughnut 
                      data={{
                        labels: ['Score', 'Remaining'],
                        datasets: [
                          {
                            label: 'Self Assessment',
                            data: [softHardSkillsData.softSkills.selfScore, 5 - softHardSkillsData.softSkills.selfScore],
                            backgroundColor: ['rgba(238, 153, 234, 0.8)', 'rgba(238, 153, 234, 0.1)'],
                            borderColor: ['rgb(238, 153, 234)', 'transparent'],
                            borderWidth: 1,
                            weight: 1,
                            spacing: 0
                          },
                          {
                            label: 'Evaluators Average',
                            data: [softHardSkillsData.softSkills.evaluatorScore, 5 - softHardSkillsData.softSkills.evaluatorScore],
                            backgroundColor: ['rgba(88, 211, 226, 0.8)', 'rgba(88, 211, 226, 0.1)'],
                            borderColor: ['rgb(88, 211, 226)', 'transparent'],
                            borderWidth: 1,
                            weight: 1,
                            spacing: 0
                          }
                        ]
                      }}
                      options={donutOptions} 
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center transform -translate-y-2">
                      <div className="text-3xl font-bold text-secondary">
                        {softHardSkillsData.softSkills.evaluatorScore}
                      </div>
                      <div className="text-sm font-medium text-gray-500">Evaluators' Score</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Hard Skills Assessment</h3>
                <div className="h-[300px] relative">
                  <div className="absolute inset-0">
                    <Doughnut 
                      data={{
                        labels: ['Score', 'Remaining'],
                        datasets: [
                          {
                            label: 'Self Assessment',
                            data: [softHardSkillsData.hardSkills.selfScore, 5 - softHardSkillsData.hardSkills.selfScore],
                            backgroundColor: ['rgba(238, 153, 234, 0.8)', 'rgba(238, 153, 234, 0.1)'],
                            borderColor: ['rgb(238, 153, 234)', 'transparent'],
                            borderWidth: 1,
                            weight: 1,
                            spacing: 0
                          },
                          {
                            label: 'Evaluators Average',
                            data: [softHardSkillsData.hardSkills.evaluatorScore, 5 - softHardSkillsData.hardSkills.evaluatorScore],
                            backgroundColor: ['rgba(88, 211, 226, 0.8)', 'rgba(88, 211, 226, 0.1)'],
                            borderColor: ['rgb(88, 211, 226)', 'transparent'],
                            borderWidth: 1,
                            weight: 1,
                            spacing: 0
                          }
                        ]
                      }}
                      options={donutOptions} 
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center transform -translate-y-2">
                      <div className="text-3xl font-bold text-secondary">
                        {softHardSkillsData.hardSkills.evaluatorScore}
                      </div>
                      <div className="text-sm font-medium text-gray-500">Evaluators' Score</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Detailed Feedback</h4>
                <ul className="space-y-3">
                  {softHardSkillsData.detailedFeedback.map((feedback: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 text-sm text-gray-600">
                      <span className="text-primary mt-1">•</span>
                      <span>{feedback}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Actionable Recommendations</h4>
                <ul className="space-y-3">
                  {softHardSkillsData.actionableRecommendations.map((recommendation: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3 text-sm text-gray-600">
                      <span className="text-secondary mt-1">•</span>
                      <span>{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}